import { Popover as AntPopover, PopoverProps as AntPopoverProps } from 'antd';
import React from 'react';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import {
  CloseIconButton,
  Header,
  PopoverContent,
  SlotContent,
  TitleText
} from './styled';

export type PopoverArrowDirection = 'up' | 'down' | 'left' | 'right';

export interface PopoverProps
  extends Omit<
    AntPopoverProps,
    'title' | 'content' | 'arrow' | 'placement' | 'color'
  > {
  /** The trigger element the popover is anchored to. */
  children: React.ReactElement;
  /** Header title. The header row (and `showCloseIcon`) only renders when provided. */
  title?: React.ReactNode;
  /** Shows the close affordance in the header. Ignored when `title` is not set. */
  showCloseIcon?: boolean;
  /** Called when the close icon is activated. */
  onClose?: () => void;
  /** Which side the arrow tail points toward. Defaults to `'up'`. */
  arrowDirection?: PopoverArrowDirection;
  /** Body content. The slot is an empty container that accepts any content — text, avatars, actions, forms, etc. */
  content?: React.ReactNode;
  className?: string;
}

const ARROW_DIRECTION_TO_PLACEMENT: Record<
  PopoverArrowDirection,
  AntPopoverProps['placement']
> = {
  down: 'top',
  up: 'bottom',
  left: 'right',
  right: 'left'
};

// `.ant-popover-inner` owns the padding/radius/shadow so the visible content
// box (padding included) matches the Figma spec exactly; see styled.tsx for
// how the inner content width is derived from it.
const POPOVER_INNER_STYLE: React.CSSProperties = {
  padding: `${theme.dime.spacing.m} ${theme.dime.spacing.l}`,
  borderRadius: theme.dime.radius.s,
  boxShadow: '0px 0px 14px 0px rgba(0, 0, 0, 0.08)'
};

export const Popover: React.FC<PopoverProps> = ({
  children,
  title,
  showCloseIcon = false,
  onClose,
  arrowDirection = 'up',
  content,
  className = '',
  ...props
}) => {
  const showHeader = Boolean(title);

  const popoverContent = (
    <PopoverContent>
      {showHeader ? (
        <Header>
          <TitleText>{title}</TitleText>
          {showCloseIcon ? (
            <CloseIconButton
              role="button"
              tabIndex={0}
              aria-label="Close popover"
              onClick={onClose}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  onClose?.();
                }
              }}
            >
              <IconWrap type={IconType.Close} size={IconSize.Small} />
            </CloseIconButton>
          ) : null}
        </Header>
      ) : null}
      <SlotContent>{content}</SlotContent>
    </PopoverContent>
  );

  return (
    <AntPopover
      className={className}
      content={popoverContent}
      placement={ARROW_DIRECTION_TO_PLACEMENT[arrowDirection]}
      color={theme.dime.colors.fill.primary}
      arrow
      overlayInnerStyle={POPOVER_INNER_STYLE}
      {...props}
    >
      {children}
    </AntPopover>
  );
};
